import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKey, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [form, setForm] = useState({ pw: "", pw2: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const invalidToken = !token || token.length < 16;

  const submit = async (e) => {
    e.preventDefault();
    if (form.pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.pw !== form.pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl font-extrabold text-slate-900 block mb-8">
          Shift<span className="text-blue-600">Flex</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-md p-8">
          {invalidToken ? (
            <div className="text-center py-6">
              <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Invalid link</h1>
              <p className="text-slate-600 mb-6">
                This reset link is missing a token. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button data-testid="reset-request-new-btn" className="bg-blue-600 hover:bg-blue-700 h-11">
                  Request a new link
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-6" data-testid="reset-success-message">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle size={32} weight="fill" className="text-emerald-600" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Password updated</h2>
              <p className="text-slate-600">Taking you back to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-1">Choose a new password</h1>
              <p className="text-slate-600 mb-6">Pick something you'll remember — at least 6 characters.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="pw" className="label-caps">New password</Label>
                  <Input
                    data-testid="reset-password-input"
                    id="pw" type="password" required autoComplete="new-password" minLength={6}
                    value={form.pw} onChange={(e) => setForm({ ...form, pw: e.target.value })}
                    className="h-12 mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="pw2" className="label-caps">Confirm password</Label>
                  <Input
                    data-testid="reset-password-confirm-input"
                    id="pw2" type="password" required autoComplete="new-password" minLength={6}
                    value={form.pw2} onChange={(e) => setForm({ ...form, pw2: e.target.value })}
                    className="h-12 mt-1.5"
                  />
                </div>
                <Button
                  data-testid="reset-submit-btn"
                  type="submit" disabled={busy}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold text-base"
                >
                  <LockKey size={18} className="mr-2" />
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
          <div className="mt-6 text-sm text-slate-600 text-center">
            Back to <Link to="/login" data-testid="link-back-to-login" className="text-blue-600 font-semibold">login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
